import { useEffect, useState } from "react";
import { Link, useSearchParams, Navigate, useNavigate } from "react-router-dom";
import { api, FILE_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import LoginPromptModal from "../components/LoginPromptModal";
import ConsultoraModal from "../components/ConsultoraModal";

// Editorial-style fallback (used until the admin uploads a custom photo).
const DEFAULT_BG = "https://images.unsplash.com/photo-1611591437281-460bfbe1220a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function TryOnEntryPage() {
  const [params] = useSearchParams();
  const produto = params.get("produto");
  const nav = useNavigate();
  const { user } = useAuth();
  const [bg, setBg] = useState(null);
  const [promptOpen, setPromptOpen] = useState(false);
  const [consultoraOpen, setConsultoraOpen] = useState(false);

  useEffect(() => {
    api.get("/settings/homepage-images").then((r) => {
      const path = r?.data?.images?.tryon_entry_bg;
      setBg(path ? FILE_URL(path) : DEFAULT_BG);
    }).catch(() => setBg(DEFAULT_BG));
  }, []);

  if (produto) {
    return <Navigate to={`/try-on/experimentar?produto=${produto}`} replace />;
  }

  // Anonymous click on "Escolher peça" → open the gentle login modal first.
  const handleChoosePiece = (e) => {
    e.preventDefault();
    if (!user) { setPromptOpen(true); return; }
    nav("/try-on/experimentar");
  };

  return (
    <div className="bg-[#F7F4ED] min-h-screen py-12 md:py-16" data-testid="tryon-entry">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Page intro — kept as is, but using #F7F4ED page bg per spec */}
        <div className="text-center mb-12 md:mb-16">
          <p className="overline text-[#B8A88A] mb-4">Try On Experience</p>
          <h1 className="font-display text-4xl md:text-6xl mb-5 leading-tight">Cada joia conta uma história.</h1>
          <p className="font-italic-display text-2xl md:text-3xl text-[#6B6B6B]">Qual será a sua?</p>
        </div>

        {/* Asymmetric editorial: 56% image-led / 44% solid dark side */}
        <div className="grid md:grid-cols-[56fr_44fr] min-h-[520px] md:min-h-[560px] overflow-hidden">
          {/* LEFT — photo bg with gradient & overlaid text */}
          <a
            href="/try-on/experimentar"
            onClick={handleChoosePiece}
            data-testid="mode-experiment"
            className="relative block min-h-[420px] md:min-h-0 group"
          >
            {bg && (
              <img
                src={bg}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
                data-testid="tryon-entry-bg-img"
              />
            )}
            {/* Gradient from page bg → transparent, anchored on the left for text legibility */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#F7F4ED] via-[#F7F4ED]/85 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-8 md:p-14 max-w-md">
              <p className="overline text-[#B8A88A] mb-4">Modo 1</p>
              <h2 className="font-display text-3xl md:text-5xl leading-[1.05] mb-5 text-[#1A1A1A]">
                Experimentar<br/>uma peça
              </h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-8 max-w-sm">
                Escolha qualquer joia do catálogo e veja, com ajuda da nossa IA, como ela fica em você.
              </p>
              <span className="inline-flex items-center gap-3 text-sm tracking-[0.18em] uppercase text-[#1A1A1A] border-b border-[#1A1A1A] pb-2 self-start group-hover:gap-5 group-hover:text-[#B8A88A] group-hover:border-[#B8A88A] transition-all duration-300">
                Escolher peça
                <span aria-hidden="true">→</span>
              </span>
            </div>
          </a>

          {/* RIGHT — solid brown-charcoal panel, no photo */}
          <Link
            to="/try-on/quiz"
            data-testid="mode-quiz"
            className="relative block bg-[#2B2620] text-white p-8 md:p-14 group overflow-hidden"
          >
            {/* Subtle inner texture using radial highlight — very faint */}
            <div className="pointer-events-none absolute inset-0 opacity-[0.07] bg-[radial-gradient(circle_at_30%_15%,#C9A66B_0%,transparent_55%)]" />
            <div className="relative h-full flex flex-col justify-end">
              {/* Diamond/lozenge mark — minimal line icon in brand gold, replaces the AI sparkle */}
              <svg
                width="36" height="36" viewBox="0 0 36 36" fill="none"
                stroke="#C9A66B" strokeWidth="1.2" strokeLinejoin="round" strokeLinecap="round"
                className="mb-8 transition-transform duration-500 group-hover:rotate-180"
                aria-hidden="true"
              >
                <path d="M18 3 L33 18 L18 33 L3 18 Z" />
                <path d="M18 3 L12 14 L24 14 Z" opacity="0.7" />
                <path d="M3 18 L12 14 M33 18 L24 14 M18 33 L12 14 M18 33 L24 14" opacity="0.5" />
              </svg>
              <p className="overline text-[#C9A66B] mb-4">Modo 2</p>
              <h2 className="font-display text-3xl md:text-5xl leading-[1.05] mb-5 text-white">
                Descobrir<br/>meu estilo
              </h2>
              <p className="text-white/70 leading-relaxed mb-8 max-w-sm">
                Responda 5 perguntas rápidas e nossa consultora seleciona as peças perfeitas para você.
              </p>
              <span className="inline-flex items-center gap-3 text-sm tracking-[0.18em] uppercase text-white border-b border-white pb-2 self-start group-hover:gap-5 group-hover:text-[#C9A66B] group-hover:border-[#C9A66B] transition-all duration-300">
                Começar quiz
                <span aria-hidden="true">→</span>
              </span>
            </div>
          </Link>
        </div>

        {/* Subtle, non-CTA help line — opens the same modal the home strip uses. */}
        <div className="mt-12 md:mt-16 text-center">
          <p className="text-sm text-white/60 leading-relaxed">
            Dificuldade para fazer as simulações ou deseja uma consulta personalizada?{" "}
            <button
              type="button"
              onClick={() => setConsultoraOpen(true)}
              className="text-white underline underline-offset-4 decoration-white/30 hover:decoration-white transition-all"
              data-testid="tryon-consultora-link"
            >
              Converse com uma de nossas consultoras&nbsp;→
            </button>
          </p>
        </div>
      </div>

      <LoginPromptModal
        open={promptOpen}
        onClose={() => setPromptOpen(false)}
        redirectTo="/try-on/experimentar"
      />
      <ConsultoraModal open={consultoraOpen} onOpenChange={setConsultoraOpen} />
    </div>
  );
}
